import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { X, Crown, Zap, Sparkles, Armchair, ArrowRightLeft } from "lucide-react";
import { api } from "@/lib/api";
import PlayerAvatar from "@/components/app/PlayerAvatar";
import { cn } from "@/lib/utils";

/**
 * TeamDetailModal — the single reusable "tap a manager, see their squad" modal.
 *
 * Consumers: LeagueDetail (Standings tap), LeagueDashboard (Roast Awards +
 * Chip Watch + Leader tile), LeagueDiff (rival grid header), etc.
 *
 * Cache contract:
 *   Query key is `["team-detail", managerId, gw]` — TanStack shares this
 *   across every consumer in the browser tab. The BACKEND then dedupes across
 *   USERS via a process-wide TTL cache: finalized GWs are cached 24h, the
 *   live GW is refreshed every 90s. See routes.py:_manager_picks_cache.
 *
 * Free Hit note: FPL's picks endpoint already returns the *temporary* FH
 * squad for the GW in which Free Hit was played, so nothing special is
 * needed on the client — the chip badge just surfaces it.
 */

const CHIP_TONE = {
  "3xc": { icon: Crown, tone: "gold", label: "Triple Captain played" },
  bboost: { icon: Armchair, tone: "green", label: "Bench Boost played" },
  freehit: { icon: Zap, tone: "amber", label: "Free Hit played" },
  wildcard: { icon: ArrowRightLeft, tone: "red", label: "Wildcard played" },
};

function ChipBadge({ chip }) {
  const meta = CHIP_TONE[chip];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <div
      data-testid="team-modal-chip-badge"
      className={cn(
        "inline-flex items-center gap-2 rounded-full px-3 py-1.5 border mono uppercase text-[10px] tracking-widest",
        meta.tone === "gold" && "border-totem-gold/50 text-totem-gold bg-totem-gold/10",
        meta.tone === "green" && "border-totem-green/50 text-totem-green bg-totem-green/10",
        meta.tone === "amber" && "border-totem-amber/50 text-totem-amber bg-totem-amber/10",
        meta.tone === "red" && "border-totem-red/50 text-totem-red bg-totem-red/10",
      )}
    >
      <Icon size={12} />
      {meta.label}
    </div>
  );
}

function PickRow({ p, isCaptain, showPoints, testid }) {
  const captainSuffix = isCaptain ? "(C)" : p.is_vice_captain ? "(V)" : "";
  return (
    <li
      data-testid={testid}
      className={cn(
        "flex items-center gap-3 px-3 py-2 rounded-lg",
        isCaptain && "bg-totem-gold/8 border border-totem-gold/30"
      )}
    >
      <PlayerAvatar player={p} size={30} />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm truncate">
          {p.web_name}
          {captainSuffix && (
            <span className={cn(
              "ml-1.5 mono text-[10px] font-bold",
              isCaptain ? "text-totem-gold" : "text-totem-textDim"
            )}>
              {captainSuffix}
            </span>
          )}
        </div>
        <div className="mono text-[10px] uppercase text-totem-textDim">
          {p.team_short} · {p.position}
        </div>
      </div>
      {showPoints && (
        <div className="text-right mono">
          {isCaptain && p.multiplier > 1 ? (
            <div
              data-testid="team-modal-captain-points"
              className="text-sm"
            >
              <span className="text-totem-textDim">{p.raw_points} pts</span>
              <span className="text-totem-textFaint mx-1">→</span>
              <span className="text-totem-gold font-bold">
                {p.multiplied_points}
                <span className="ml-1 text-[10px]">(C)</span>
              </span>
            </div>
          ) : (
            <div className="text-sm font-semibold">
              {p.multiplier === 0 ? (
                <span className="text-totem-textFaint">{p.raw_points}</span>
              ) : (
                p.multiplied_points
              )}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

export default function TeamDetailModal({ open, onClose, managerId, gw, entryName, playerName }) {
  const enabled = open && !!managerId;
  const { data, isLoading, error } = useQuery({
    queryKey: ["team-detail", managerId, gw ?? "current"],
    queryFn: async () =>
      (await api.get(`/manager/${managerId}/picks`, { params: gw ? { gw } : undefined })).data,
    enabled,
    // TanStack side: don't refetch aggressively, backend already dedupes.
    staleTime: 90_000,
  });

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const chip = data?.active_chip;
  const benchBoostActive = data?.bench_boost_active;
  const freeHitActive = data?.free_hit_active;
  const showPoints = !!data?.gw;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      data-testid="team-detail-modal"
      className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center p-0 sm:p-4"
    >
      <div
        className="absolute inset-0 bg-totem-bg/85 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full sm:max-w-md max-h-[90vh] flex flex-col totem-card animate-fade-up overflow-hidden rounded-t-2xl sm:rounded-2xl">
        <header className="flex items-start justify-between p-4 border-b border-totem-borderSoft">
          <div className="min-w-0 flex-1">
            <div className="display-heading text-lg truncate">
              {entryName || data?.manager?.team_name || "Manager"}
            </div>
            <div className="mono text-[10px] uppercase text-totem-textDim mt-0.5 truncate">
              {playerName || data?.manager?.player_name}
              {data?.gw ? ` · GW${data.gw}` : ""}
              {data?.manager?.event_points != null && ` · ${data.manager.event_points} pts`}
              {data?.manager?.event_transfers_cost > 0 && (
                <span className="text-totem-red"> · −{data.manager.event_transfers_cost} hit</span>
              )}
            </div>
          </div>
          <button
            data-testid="team-modal-close"
            onClick={onClose}
            className="text-totem-textDim hover:text-totem-text transition p-1 -m-1"
          >
            <X size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {chip && (
            <div className="flex">
              <ChipBadge chip={chip} />
            </div>
          )}

          {isLoading && (
            <div className="totem-card-inner h-64 animate-pulse" />
          )}

          {error && (
            <div className="totem-card-inner p-4 text-totem-red text-sm">
              Could not load squad — {String(error?.response?.data?.detail || error.message)}
            </div>
          )}

          {data?.message && !isLoading && (
            <div className="totem-card-inner p-4 text-center border-totem-amber/30">
              <Sparkles className="mx-auto text-totem-amber mb-2" size={20} />
              <p className="text-sm text-totem-textDim">{data.message}</p>
            </div>
          )}

          {data?.starters && (
            <section>
              <div className="flex items-center justify-between mb-2">
                <div className="label-caps">
                  {freeHitActive ? "Free Hit XV" : "Starting XI"}
                </div>
                {freeHitActive && (
                  <span className="mono text-[9px] uppercase text-totem-amber">
                    Temporary squad — reverts next GW
                  </span>
                )}
              </div>
              <ul data-testid="team-modal-starters" className="space-y-1">
                {data.starters.map((p) => (
                  <PickRow
                    key={p.id}
                    p={p}
                    isCaptain={p.is_captain}
                    showPoints={showPoints}
                    testid={p.is_captain ? "team-modal-captain-row" : undefined}
                  />
                ))}
              </ul>
            </section>
          )}

          {data?.bench && data.bench.length > 0 && (
            <section data-testid={benchBoostActive ? "team-modal-bench-boosted" : "team-modal-bench"}>
              <div className="flex items-center justify-between mb-2">
                <div className={cn(
                  "label-caps",
                  benchBoostActive && "text-totem-green"
                )}>
                  {benchBoostActive ? "Bench (counts toward total)" : "Bench"}
                </div>
                <span className="mono text-[10px] uppercase text-totem-textDim">
                  {benchBoostActive ? (
                    <span className="text-totem-green">+{data.bench_points} pts</span>
                  ) : (
                    <>{data.bench_points || 0} pts (not counted)</>
                  )}
                </span>
              </div>
              <ul className="space-y-1">
                {data.bench.map((p) => (
                  <PickRow
                    key={p.id}
                    p={{ ...p, multiplier: benchBoostActive ? 1 : p.multiplier }}
                    isCaptain={false}
                    showPoints={showPoints}
                  />
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
